import { Box, Center, Image, Progress, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

interface LoadingScreenProps {
  /** When true, the progress will complete instantly to 100% */
  shouldCompleteProgress?: boolean;
  /** Called when progress reaches 100% and the delay period is finished */
  onProgressComplete?: () => void;
}

export const LoadingScreen = ({
  shouldCompleteProgress = false,
  onProgressComplete,
}: LoadingScreenProps) => {
  const [progress, setProgress] = useState(0);
  const completionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        // If we should complete progress instantly, jump to 100%
        if (shouldCompleteProgress && prev < 100) {
          // Show 100% for a brief moment before calling completion
          if (completionTimeoutRef.current) {
            clearTimeout(completionTimeoutRef.current);
          }
          completionTimeoutRef.current = setTimeout(() => {
            onProgressComplete?.();
          }, 300);
          return 100;
        }

        if (prev < 95) {
          // Use elegant easing function for perceived performance
          // Fast at start, natural slowdown, holds near end
          const increment = (1 - prev / 100) ** 2 * 5;
          const newProgress = prev + increment;

          // Don't exceed 95% in this phase
          return Math.min(newProgress, 95);
        }
        if (!shouldCompleteProgress) {
          // From 95% to 100% should take 10 minutes (600 seconds)
          // 5% progress over 600 seconds = 0.00833% per second
          // With 80ms intervals: 0.00833% / (1000ms/80ms) = 0.000667% per update
          const slowIncrement = 0.000_667;
          const newProgress = prev + slowIncrement;

          // Cap at 100%
          return Math.min(newProgress, 100);
        }

        return prev;
      });
    }, 80); // Update every 80ms for smooth animation

    return () => {
      clearInterval(interval);
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
      }
    };
  }, [shouldCompleteProgress, onProgressComplete]);

  return (
    <Center
      bg="dark.9"
      data-test-id="loading-screen"
      h="100dvh"
      pos="relative"
      w="100dvw"
    >
      {/* Purple circle */}
      <Box
        data-test-id="loading-screen-bg-purple"
        style={{
          position: 'absolute',
          width: 320,
          height: 320,
          borderRadius: '50%',
          opacity: 0.9,
          background: '#562252',
          filter: 'blur(96px)',
          top: '50%',
          left: '40%',
          translate: '-50% -50%',
        }}
      />
      {/* Red circle */}
      <Box
        data-test-id="loading-screen-bg-red"
        style={{
          position: 'absolute',
          width: 260,
          height: 260,
          borderRadius: '50%',
          opacity: 0.7,
          background: '#BA2751',
          filter: 'blur(96px)',
          top: '60%',
          right: '30%',
          translate: '-50% -50%',
        }}
      />
      <Image
        alt="Logo"
        bottom={32}
        data-test-id="loading-logo"
        fit="contain"
        h={100}
        left="50%"
        loading="eager"
        pos="absolute"
        src="/game/logo_rat.webp"
        style={{ zIndex: 10, translate: '-50% -50%' }}
        w="auto"
      />

      {/* Progress bar */}
      <Stack
        align="center"
        gap={8}
        left="50%"
        pos="absolute"
        px={16}
        style={{ translate: '-50% -50%', zIndex: 10 }}
        top="50%"
        w="100%"
      >
        <Text
          c="yellow"
          data-test-id="loading-progress-text"
          fw={700}
          size="xl"
          ta="center"
        >
          {progress.toFixed(2)}%
        </Text>
        <Progress
          color="grape"
          data-test-id="loading-progress"
          h={24}
          maw={400}
          radius="xl"
          size="lg"
          striped={false}
          style={{
            background: '#292933',
            border: '4px solid #EE8C08',
            borderRadius: '999px',
            filter: 'drop-shadow(0px 0px 12px rgba(255, 255, 0, 0.4))',
          }}
          styles={(theme) => ({
            section: {
              border: `2px solid ${theme.colors.dark[6]}`,
            },
          })}
          value={progress}
          w="100%"
        />
      </Stack>
    </Center>
  );
};
